import { Baby, Backpack, Sunrise, Sunset, CalendarDays, BookOpen, Palette } from "lucide-react";
import { Reveal, Chapter, scrollToId } from "@/components/Shared";

const SERVICES = [
  {
    id: "service-2-5",
    icon: Baby,
    title: "Childcare · Ages 2–5",
    desc: "A nurturing environment with age-appropriate learning, play, social development, arts and crafts, story time, and structured activities.",
    tag: "Early Years",
  },
  {
    id: "service-6-12",
    icon: Backpack,
    title: "School-Age Care · Ages 6–12",
    desc: "Supervised care for school-age children in a safe and engaging environment they'll look forward to.",
    tag: "School Age",
  },
  {
    id: "service-before",
    icon: Sunrise,
    title: "Before-School Care",
    desc: "Dependable early-morning care designed for families who need childcare before the school day begins.",
    tag: "Mornings",
  },
  {
    id: "service-after",
    icon: Sunset,
    title: "After-School Care",
    desc: "A safe place for children after school with supervised activities, recreation, quiet time, and opportunities to complete homework.",
    tag: "Afternoons",
  },
  {
    id: "service-weekend",
    icon: CalendarDays,
    title: "Weekend Childcare",
    desc: "Saturday and Sunday childcare for parents and guardians who work weekends or need dependable weekend care.",
    tag: "Sat & Sun",
  },
  {
    id: "service-homework",
    icon: BookOpen,
    title: "Homework Support",
    desc: "A quiet and supportive environment where children can work on homework and school assignments with confidence.",
    tag: "Academics",
  },
  {
    id: "service-enrichment",
    icon: Palette,
    title: "Educational & Enrichment",
    desc: "Age-appropriate learning activities, reading, arts and crafts, games, creative play, and social-development activities.",
    tag: "Enrichment",
  },
];

const Services = () => (
  <section id="services" data-testid="services-section" className="relative bg-cream/60 py-24 sm:py-32">
    <div className="mx-auto max-w-7xl px-5 sm:px-8">
      <Reveal className="max-w-2xl">
        <Chapter num="02" label="Programs & Services" />
        <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-medium tracking-tight leading-[1.12] text-deepteal-dark">
          Care that fits <em className="italic text-terracotta">real family life</em> — weekdays and weekends.
        </h2>
        <p className="mt-5 text-base sm:text-lg leading-relaxed text-slate-600">
          From toddler days to school-age afternoons, our programs support children ages 2–12 with
          structure, warmth, and learning through play — a true childcare home for Halethorpe families.
        </p>
      </Reveal>

      <div className="mt-14 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
        {SERVICES.map((s, i) => (
          <Reveal
            key={s.id}
            delay={0.06 * i}
            className={i === 0 ? "sm:col-span-2 lg:col-span-1" : ""}
          >
            <article
              data-testid={s.id}
              className="group h-full rounded-3xl border border-amber-900/10 bg-white/80 p-7 backdrop-blur-sm transition-all duration-500 hover:-translate-y-1.5 hover:shadow-2xl hover:shadow-deepteal/15 hover:border-terracotta/30"
            >
              <div className="flex items-start justify-between">
                <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-deepteal/8 bg-amber-100 text-terracotta transition-colors duration-500 group-hover:bg-terracotta group-hover:text-amber-50">
                  <s.icon className="h-6 w-6" strokeWidth={1.8} />
                </div>
                <span className="font-mono text-[10px] font-semibold uppercase tracking-[0.2em] text-amber-700/80 border border-amber-700/20 rounded-full px-3 py-1">
                  {s.tag}
                </span>
              </div>
              <h3 className="mt-5 font-serif text-xl sm:text-2xl font-medium text-deepteal-dark">
                {s.title}
              </h3>
              <p className="mt-3 text-sm leading-relaxed text-slate-600">{s.desc}</p>
            </article>
          </Reveal>
        ))}

        <Reveal delay={0.42}>
          <div className="flex h-full flex-col justify-center rounded-3xl bg-deepteal p-7 text-amber-50 transition-all duration-500 hover:-translate-y-1.5 hover:shadow-2xl hover:shadow-deepteal/30">
            <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-amber-300">
              Not sure which fits?
            </p>
            <p className="mt-3 font-serif text-2xl leading-snug">
              Tell us your schedule — we'll help you find the right piece.
            </p>
            <a
              data-testid="services-cta-contact"
              href="#enrollment"
              onClick={(e) => { e.preventDefault(); scrollToId("#enrollment"); }}
              className="mt-5 inline-flex w-fit items-center gap-2 rounded-full bg-amber-400 px-5 py-2.5 text-sm font-bold text-deepteal-dark transition-all duration-300 hover:bg-amber-300"
            >
              Start Enrollment
            </a>
          </div>
        </Reveal>
      </div>
    </div>
  </section>
);

export default Services;
